import { __assign } from "tslib";
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { createRocket } from "../_util/create-rocket";
import { Justify } from "../justify";
import { Button } from "../button";
import { FadeTransition, SlideTransition } from "../transition";
import { useOutsideClick } from "../_util/use-outside-click";
import { H3 } from "../heading";
import { useConfig } from "../_util/config-context";
import { useVisibleTransition } from "../_util/use-visible-transition";
import { callBoth } from "../_util/call-both";
import { noop } from "../_util/noop";
import { uuid } from "../_util/uuid";
import { zStack } from "../_util/z-stack";
import { BackDrop } from "../backdrop";
import { useOverlayFocus } from "../_util/use-overlay-focus";
import { getOverlayRoot } from "../_util/get-overlay-root";
import { useTranslation } from "../i18n";
import { mergeRefs } from "../util";
export var Drawer = React.forwardRef(function Drawer(_a, fRef) {
    var _b, _c;
    var className = _a.className, _d = _a.style, style = _d === void 0 ? {} : _d, children = _a.children, visible = _a.visible, _e = _a.onClose, onClose = _e === void 0 ? noop : _e, _f = _a.onExited, onExited = _f === void 0 ? noop : _f, size = _a.size, placement = _a.placement, footer = _a.footer, title = _a.title, subTitle = _a.subTitle, _g = _a.subtitle, subtitle = _g === void 0 ? subTitle : _g, extra = _a.extra, disableCloseIcon = _a.disableCloseIcon, disableAnimation = _a.disableAnimation, _h = _a.outerClickClosable, outerClickClosable = _h === void 0 ? true : _h, _j = _a.destroyOnClose, destroyOnClose = _j === void 0 ? true : _j, _k = _a.showMask, showMask = _k === void 0 ? false : _k, _l = _a.maskStyle, maskStyle = _l === void 0 ? {} : _l, popupContainer = _a.popupContainer;
    var _m = useConfig(), classPrefix = _m.classPrefix, globalPopupContainer = _m.popupContainer;
    var t = useTranslation();
    var ref = useRef(null);
    var focusRef = useRef(null);
    var idRef = useRef(uuid());
    var firstRender = useRef(!!visible);
    var _o = useOutsideClick(ref), listen = _o.listen, ignoreProps = _o.ignoreProps;
    listen(function () {
        return outerClickClosable && visible && zStack.top === idRef.current && onClose();
    });
    if (visible && !firstRender.current) {
        firstRender.current = true;
    }
    var width = size === "l" ? 800 : 320;
    var from = placement === "left" ? 0 - width : width;
    var showHeader = title || subtitle || extra || !disableCloseIcon;
    var _p = useVisibleTransition(visible), contentIn = _p.contentIn, shouldContentEnter = _p.shouldContentEnter, shouldContentRender = _p.shouldContentRender, onContentExit = _p.onContentExit;
    useEffect(function () {
        if (contentIn) {
            var id_1 = idRef.current;
            zStack.push(id_1);
            return function () {
                zStack.remove(id_1);
            };
        }
        return noop;
    }, [contentIn]);
    useOverlayFocus(visible, focusRef);
    if (!shouldContentRender &&
        (destroyOnClose || (!destroyOnClose && !firstRender.current))) {
        return null;
    }
    return ReactDOM.createPortal(React.createElement("div", { tabIndex: -1, ref: focusRef, className: classNames((_b = {},
            _b[classPrefix + "-drawer-open"] = visible,
            _b)), style: {
            display: !destroyOnClose && !contentIn ? "none" : undefined,
        } },
        showMask && (React.createElement(FadeTransition, { in: shouldContentEnter, opacity: 1 },
            React.createElement(BackDrop, { zIndex: typeof style.zIndex !== "undefined" ? style.zIndex : 10, style: maskStyle }))),
        React.createElement(SlideTransition, { in: shouldContentEnter, from: [from, 0], timeout: disableAnimation ? 0 : undefined, unmountOnExit: destroyOnClose, onExited: callBoth(onExited, onContentExit) },
            React.createElement("div", __assign({ ref: mergeRefs(ref, fRef), style: style, className: classNames(classPrefix + "-drawer", className, (_c = {},
                    _c[classPrefix + "-drawer--left"] = placement === "left",
                    _c["size-l"] = size === "l",
                    _c)) }, ignoreProps),
                showHeader && (React.createElement("div", { className: classPrefix + "-drawer__header" },
                    React.createElement(Justify, { left: React.createElement(DrawerTitle, { title: title, subtitle: subtitle, classPrefix: classPrefix }), right: React.createElement(React.Fragment, null,
                            extra,
                            !disableCloseIcon && (React.createElement(Button, { icon: "close", htmlType: "button", onClick: onClose, "aria-label": t.closeText }))) }))),
                React.createElement(DrawerBody, null, children),
                footer && React.createElement(DrawerFooter, null, footer)))), getOverlayRoot(popupContainer || globalPopupContainer));
});
Drawer.displayName = "Drawer";
function DrawerTitle(_a) {
    var title = _a.title, subtitle = _a.subtitle, classPrefix = _a.classPrefix;
    var sub = typeof subtitle === "string" ? (React.createElement("span", { className: classPrefix + "-card__subtitle" }, subtitle)) : (subtitle);
    if (typeof title === "string") {
        return (React.createElement(H3, null,
            title,
            sub));
    }
    return (React.createElement(React.Fragment, null,
        title,
        sub));
}
DrawerTitle.displayName = "DrawerTitle";
var DrawerBody = createRocket("DrawerBody", "div.@{prefix}-drawer__body", "div.@{prefix}-drawer__body-inner");
var DrawerFooter = createRocket("DrawerFooter", "div.@{prefix}-drawer__footer");
//# sourceMappingURL=Drawer.js.map